import { createContext, useContext, useEffect, useState } from 'react'
import { shallowEqual, useSelector, useDispatch } from 'react-redux'

import { useQuery } from '@/hooks'
import {
	CATALOG_IDS,
	ALLOWED_ISOCODE_INT,
	COOKIE_LANGUAGE_KEY,
	COOKIE_CATALOG_ID_KEY,
	DEFAULT_CATALOG,
	DEFAULT_LANGUAGE,
	LANGUAGE_CODES
} from '@/constants'
import {
	getCookie,
	setLanguageCookie,
	setCatalogIdCookie,
	setCatalogNameCookie
} from '@/utils'

import { catalogsActions, i18nActions, uiActions, authActions } from '@/redux/actions'
import { REFRESH_TOKEN } from '@/redux/actions/auth.actions'
import { ModalContext } from '@/contexts/ModalContext'
import { authMiddleware } from '@/utils/authClient.utils'
import useAuth from '@/hooks/useAuth'
import ApiCaller from '@/utils/apiCaller'

const AuthContext = createContext()

const AuthProvider = (props) => {
	const { children } = props

	const query = useQuery()
	const dispatch = useDispatch()
	const { modalUnmount } = useContext(ModalContext)
	const { authenticate, logout } = useAuth()

	const userAccount = useSelector(state => state.auth.account, shallowEqual)
	const userIsLogged = useSelector(state => state.auth.logged, shallowEqual)
	const defaultCatalog = useSelector(state => state.catalogs.defaultCatalog, shallowEqual)
	const showModalAuth = useSelector(state => state.auth.showModalAuth, shallowEqual)

	const navigation = useSelector(state => state.ui.navigation, shallowEqual)

	const [cookieLangSession, setCookieLangSession] = useState(getCookie(COOKIE_LANGUAGE_KEY))

	useEffect(async () => {
		authMiddleware(logout)
		dispatch(authActions.showModalAuth(false))

		const res = await dispatch(authActions.refreshToken())

		dispatch(uiActions.setNavigation({ hasBeenActive: res?.type === REFRESH_TOKEN.SUCCESS }))

		let catalogId = null

		try {
			const response = await ApiCaller.call('v2', 'GET', '/cookie', null, {credentials: "include"})
			catalogId = response?.[COOKIE_CATALOG_ID_KEY]
		} catch (error) {
		}
		if (catalogId || query.get('catalog') || getCookie(COOKIE_CATALOG_ID_KEY)) {
			const catalog =
				Object.values(CATALOG_IDS).find((catalogId) => catalogId === CATALOG_IDS[query.get('catalog')]) ||
				+catalogId ||
				+getCookie(COOKIE_CATALOG_ID_KEY)

			const lang = getDefaultLanguage(catalog)
			setCatalogAndLanguage(lang, catalog)
			setCookieLangSession(lang)
		} else {
			dispatch(catalogsActions.getDefaultCatalog())
		}
	}, [])

	useEffect(() => {
		if (userIsLogged) {
			dispatch(authActions.getAccountDetails())
		}
	}, [userIsLogged])

	useEffect(() => {
		if (userAccount) {
			dispatch(authActions.logged())
		}
	}, [userAccount])

	useEffect(() => {
		if (defaultCatalog?.catalogId) {
			const lang = getDefaultLanguage(defaultCatalog?.catalogId)
			setCatalogAndLanguage(lang, defaultCatalog?.catalogId)
		}
	}, [defaultCatalog?.catalogId])

	useEffect(() => {
		if (!userAccount || navigation.preferredByUser) {
			return
		}

		const language =
			userAccount.lang ||
			Object.values(LANGUAGE_CODES).find((language) => language === query.get('language')) ||
			getCookie(COOKIE_LANGUAGE_KEY) ||
			DEFAULT_LANGUAGE
		const catalog =
			+userAccount.catalog ||
			+Object.values(CATALOG_IDS).find((catalogId) => catalogId === CATALOG_IDS[query.get('catalog')]) ||
			+getCookie(COOKIE_CATALOG_ID_KEY) ||
			DEFAULT_CATALOG

		if (navigation.hasBeenActive && cookieLangSession !== userAccount.lang) {
			dispatch(uiActions.setNavigation({ language: getCookie(COOKIE_LANGUAGE_KEY), catalog: +getCookie(COOKIE_CATALOG_ID_KEY) }))
			dispatch(catalogsActions.selectCatalog(+getCookie(COOKIE_CATALOG_ID_KEY)))
		} else {
			setCatalogAndLanguage(language, catalog)
		}
	}, [userAccount])

	const setCatalogAndLanguage = (language, catalog) => {
		dispatch(i18nActions.setLocale(language))
		dispatch(uiActions.setNavigation({ language, catalog }))
		dispatch(catalogsActions.selectCatalog(catalog))
		setLanguageCookie(language)
		setCatalogIdCookie(catalog)
		setCatalogNameCookie(catalog)
	}

	const getDefaultLanguage = (catalog) => {
		if (query.get('language') || getCookie(COOKIE_LANGUAGE_KEY)) {
			return Object.values(LANGUAGE_CODES).find((language) => language === query.get('language')) || getCookie(COOKIE_LANGUAGE_KEY)
		}
		if (catalog === CATALOG_IDS.USA) {
			return 'us'
		}
		return ALLOWED_ISOCODE_INT.includes(window.navigator.language.split('-')[0])
			? window.navigator.language.split('-')[0]
			: defaultCatalog?.lang?.isoCode || DEFAULT_LANGUAGE
	}

	useEffect(() => {
		if (showModalAuth) {
			authenticate()
		} else {
			modalUnmount()
		}
	}, [showModalAuth])


	return (
		<AuthContext.Provider
			value={{}}
		>
			{children}
		</AuthContext.Provider>
	)
}

export { AuthContext, AuthProvider }
